import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
    ArrowLeft, Loader2, Download, RefreshCw, Lock, TrendingUp,
    Users as UsersIcon, CreditCard, Wallet, BookOpen, LifeBuoy, ShieldCheck,
    Globe, Radar, AlertTriangle, CheckCircle2, Baby, UserX, RotateCcw,
} from 'lucide-react';
import useSEO from '@/hooks/useSEO';

/**
 * /admin/operations — Comprehensive operating dashboard.
 *
 * Six modules: Executive, Users, Subscriptions, Finance, Content, Support.
 * Visible to super-admin (full access) and to the sub-admin role granted
 * permission for each module. Module gating is enforced server-side; we
 * just hide tabs we cannot load to reduce 403 noise in the UI.
 */
const MODULES = [
    { slug: 'overview',   label: 'Executive overview',     icon: TrendingUp,  roles: ['*'] },
    { slug: 'audience',   label: 'Audience map',           icon: Globe,       roles: ['*'] },
    { slug: 'users',      label: 'Users & learning',       icon: UsersIcon,   roles: ['content', 'support'] },
    { slug: 'subs',       label: 'Subscriptions',          icon: CreditCard,  roles: ['finance'] },
    { slug: 'finance',    label: 'Finance & invoices',     icon: Wallet,      roles: ['finance'] },
    { slug: 'content',    label: 'Content performance',    icon: BookOpen,    roles: ['content'] },
    { slug: 'support',    label: 'Support & platform',     icon: LifeBuoy,    roles: ['support'] },
    { slug: 'attribution',label: 'Attribution insights',   icon: Radar,       roles: [] }, // super only
    { slug: 'deletions',  label: 'Deletions & churn',      icon: UserX,       roles: [] }, // super only
    { slug: 'admins',     label: 'Admin accounts',         icon: ShieldCheck, roles: [] }, // super only
    { slug: 'exports',    label: 'CSV exports',            icon: Download,    roles: ['finance', 'support'] },
];

function isSuper(user) {
    return user?.role === 'admin' && (!user.admin_role || user.admin_role === 'super_admin');
}

function canSee(mod, user) {
    if (isSuper(user)) return true;
    if (user?.role !== 'admin') return false;
    if (mod.roles.includes('*')) return true;
    return mod.roles.includes(user.admin_role);
}

export default function AdminOperations() {
    const { user } = useAuth();
    const [tab, setTab] = useState('overview');
    useSEO({ title: 'Operations | Linguistic Quest', path: '/admin/operations', noindex: true });

    if (!user || user.role !== 'admin') {
        return <main className="max-w-3xl mx-auto px-6 py-20 text-center text-lq-ink-500">Admin access required.</main>;
    }
    const visibleModules = MODULES.filter((m) => canSee(m, user));

    return (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="page-admin-operations">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <Link to="/admin" className="text-sm text-lq-ink-500 hover:text-lq-purple-700 inline-flex items-center gap-1"><ArrowLeft size={14}/> Admin home</Link>
                    <h1 className="mt-1 font-display text-3xl font-semibold text-lq-ink-900">Operations</h1>
                    <p className="text-sm text-lq-ink-500">
                        Role: <strong className="text-lq-purple-700">{isSuper(user) ? 'super_admin' : (user.admin_role || 'admin')}</strong>
                    </p>
                </div>
            </div>

            <nav className="mt-6 border-b border-lq-ink-100 flex gap-1 flex-wrap overflow-x-auto" role="tablist">
                {visibleModules.map((m) => {
                    const Icon = m.icon;
                    return (
                        <button
                            key={m.slug} onClick={() => setTab(m.slug)} role="tab"
                            className={`px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 inline-flex items-center gap-1.5 ${
                                tab === m.slug
                                    ? 'border-lq-purple-500 text-lq-purple-900'
                                    : 'border-transparent text-lq-ink-500 hover:text-lq-ink-900'
                            }`}
                            data-testid={`ops-tab-${m.slug}`}
                        >
                            <Icon size={14}/> {m.label}
                        </button>
                    );
                })}
            </nav>

            <div className="mt-6">
                {tab === 'overview' && <OverviewModule />}
                {tab === 'audience' && <AudienceModule />}
                {tab === 'users' && <UsersModule />}
                {tab === 'subs' && <SubsModule />}
                {tab === 'finance' && <FinanceModule />}
                {tab === 'content' && <ContentModule />}
                {tab === 'support' && <SupportModule />}
                {tab === 'attribution' && <AttributionModule />}
                {tab === 'deletions' && <DeletionsModule />}
                {tab === 'admins' && <AdminsModule />}
                {tab === 'exports' && <ExportsModule user={user} />}
            </div>
        </main>
    );
}


// =================== UI primitives ===========================================

function Stat({ label, value, sub, accent }) {
    return (
        <div className="bg-white rounded-2xl border border-lq-ink-100 p-5" data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            <div className="text-[11px] text-lq-ink-500 uppercase tracking-wider font-semibold">{label}</div>
            <div className={`mt-1 font-display text-2xl ${accent || 'text-lq-ink-900'}`}>{value}</div>
            {sub && <div className="mt-1 text-xs text-lq-ink-500">{sub}</div>}
        </div>
    );
}

function PendingTile({ label, reason }) {
    return (
        <div className="bg-lq-ink-100/40 rounded-2xl border border-dashed border-lq-ink-300 p-5">
            <div className="text-[11px] text-lq-ink-500 uppercase tracking-wider font-semibold inline-flex items-center gap-1"><Lock size={11}/> {label}</div>
            <div className="mt-1 font-display text-lg text-lq-ink-500">Coming soon</div>
            {reason && <div className="mt-1 text-xs text-lq-ink-500">{reason}</div>}
        </div>
    );
}

function useApi(url, deps = []) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const reload = useMemo(() => () => {
        setLoading(true);
        api.get(url).then(({ data }) => { setData(data); setErr(null); })
            .catch((e) => setErr(e?.response?.data?.detail || 'Failed to load'))
            .finally(() => setLoading(false));
    }, [url]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { reload(); }, deps);
    return { data, err, loading, reload };
}

function gbp(pence) {
    if (typeof pence !== 'number') return '—';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100);
}

function fmt(value, options = {}) {
    if (value === null || value === undefined) return '—';
    if (options.pct) return `${(value * 100).toFixed(options.decimals ?? 1)}%`;
    return value.toLocaleString();
}

function asPlaceholderOrValue(v, render) {
    if (v && typeof v === 'object' && v.telemetry_pending) {
        return <PendingTile label={render.label} reason={v.reason} />;
    }
    return render.real;
}


// =================== Module: Executive Overview ==============================

function OverviewModule() {
    const { data, err, loading, reload } = useApi('/admin/overview');
    const [sendingDigest, setSendingDigest] = useState(false);
    const triggerDigest = async () => {
        if (!window.confirm('Send Mission Control weekly digest email to julian@linguisticquest.com right now?')) return;
        setSendingDigest(true);
        try {
            const { data: res } = await api.post('/admin/mission-control/digest/run', { force: true });
            if (res.sent) {
                toast.success(`Digest sent to ${res.recipient} (week ${res.week}).`);
            } else {
                toast.warning(`Not sent — ${res.reason || 'unknown'}.`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not send digest');
        } finally { setSendingDigest(false); }
    };
    if (loading) return <Loading/>;
    if (err) return <ErrorBox msg={err}/>;
    return (
        <section className="space-y-6" data-testid="module-overview">
            <Header
                title="Executive overview"
                sub={`As of ${new Date(data.as_of).toLocaleString()}`}
                onReload={reload}
                action={
                    <button
                        onClick={triggerDigest}
                        disabled={sendingDigest}
                        className="px-4 py-2 text-sm font-semibold bg-lq-purple-500 text-white rounded-full hover:bg-lq-purple-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                        data-testid="trigger-mission-control-digest"
                    >
                        {sendingDigest && <Loader2 size={14} className="animate-spin"/>} Send Mission Control digest
                    </button>
                }
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Total users" value={fmt(data.total_users)} sub={`+${fmt(data.new_users_24h)} in 24h`} />
                <Stat label="DAU" value={fmt(data.dau)} sub="last 24h"/>
                <Stat label="MAU" value={fmt(data.mau)} sub="last 30d"/>
                <Stat label="DAU/MAU" value={`${(data.dau_mau_ratio * 100).toFixed(0)}%`} sub="stickiness"/>

                <Stat label="Paid subscribers" value={fmt(data.paid_subscribers)} accent="text-lq-purple-700"/>
                <Stat label="MRR" value={gbp(data.mrr_pence)} sub={`ARR ${gbp(data.arr_pence)}`} accent="text-lq-purple-700"/>
                <Stat label="Total collected" value={gbp(data.total_collected_pence)} sub="invoices + gift cards lifetime"/>
                {asPlaceholderOrValue(data.ad_revenue_pence, {
                    label: 'Ad revenue',
                    real: <Stat label="Ad revenue" value={gbp(data.ad_revenue_pence)}/>,
                })}

                <Stat label="Churn (30d)" value={`${(data.churn_rate_30d * 100).toFixed(1)}%`} sub="proxy: cancelled / paid"/>
                <Stat label="Active streaks" value={fmt(data.active_streaks)} sub="DAU proxy"/>
                <Stat label="Lessons today" value={fmt(data.lessons_completed_today)} sub={`${fmt(data.lessons_completed_30d)} in 30d`}/>
                <Stat label="Support open" value={fmt(data.support_open)} sub={`${fmt(data.support_open_24h)} in last 24h`} accent={data.support_open > 10 ? 'text-lq-error' : 'text-lq-ink-900'}/>

                <Stat label="Payment failures (30d)" value={fmt(data.payment_failures_30d)} accent={data.payment_failures_30d > 0 ? 'text-lq-error' : 'text-lq-success'}/>
                <Stat label="New users (7d)" value={fmt(data.new_users_7d)}/>
                <Stat label="New users (30d)" value={fmt(data.new_users_30d)}/>
                <Stat label="New users (24h)" value={fmt(data.new_users_24h)}/>
            </div>
        </section>
    );
}


// =================== Module: Users & Learning ================================

function UsersModule() {
    const { data, err, loading, reload } = useApi('/admin/insights/users');
    if (loading) return <Loading/>;
    if (err) return <ErrorBox msg={err}/>;

    // Build a tiny sparkline from cohorts
    const cohorts = data.new_user_cohorts_30d || [];
    return (
        <section className="space-y-6" data-testid="module-users">
            <Header title="User &amp; learning analytics" sub="Last 30 days" onReload={reload}/>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Registered" value={fmt(data.registered_users)} sub={`${fmt(data.parents)} parents · ${fmt(data.students)} students`}/>
                <Stat label="Lessons (24h)" value={fmt(data.lessons_completed_24h)}/>
                <Stat label="Lessons (7d)" value={fmt(data.lessons_completed_7d)}/>
                <Stat label="Lessons (30d)" value={fmt(data.lessons_completed_30d)}/>
                <Stat label="XP total" value={fmt(data.xp_total)} sub="users + child profiles" accent="text-lq-purple-700"/>
                <PendingTile label="Placement tests" reason={data.placement_test_completions?.reason}/>
                <PendingTile label="Lesson drop-off" reason={data.lesson_drop_off_by_unit?.reason}/>
                <PendingTile label="Course completion" reason={data.course_completion_rates?.reason}/>
            </div>

            <Card title="New user cohorts · last 30d">
                {cohorts.length === 0 ? (
                    <p className="text-sm text-lq-ink-500">No new sign-ups in the last 30 days.</p>
                ) : (
                    <Sparkline series={cohorts.map((c) => c.new_users)} labels={cohorts.map((c) => c.date)} />
                )}
            </Card>

            <Card title="Active learners by language · last 30d">
                <BarTable rows={data.active_by_language_30d || []}
                          cols={[
                              { key: 'language_slug', label: 'Language', className: 'capitalize' },
                              { key: 'active_learners', label: 'Active', right: true },
                              { key: 'plays', label: 'Plays', right: true, bar: true },
                          ]}
                          empty="No language activity yet." />
            </Card>
        </section>
    );
}


// =================== Module: Subscriptions ===================================

function SubsModule() {
    const { data, err, loading, reload } = useApi('/admin/insights/subs');
    if (loading) return <Loading/>;
    if (err) return <ErrorBox msg={err}/>;
    return (
        <section className="space-y-6" data-testid="module-subs">
            <Header title="Subscriptions &amp; monetisation" onReload={reload}/>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Trialing" value={fmt(data.trials)}/>
                <Stat label="Active" value={fmt(data.active)} accent="text-lq-success"/>
                <Stat label="Past due" value={fmt(data.past_due)} accent={data.past_due > 0 ? 'text-lq-error' : ''}/>
                <Stat label="Cancelled (30d)" value={fmt(data.cancelled_30d)}/>

                <Stat label="Free→paid" value={fmt(data.free_to_paid_conversion, { pct: true })} sub="paid / parents"/>
                <Stat label="Trial starts (30d)" value={fmt(data.trial_starts_30d)}/>
                <Stat label="Trial → paid (30d)" value={fmt(data.trial_to_paid_30d)}/>
                <Stat label="ARPU" value={gbp(data.arpu_pence)} sub="per paid user / mo" accent="text-lq-purple-700"/>

                <Stat label="LTV (12mo proxy)" value={gbp(data.ltv_pence)} accent="text-lq-purple-700"/>
                <Stat label="Ad impressions" value={fmt(data.ad_impressions)}/>
                <Stat label="Ad CTR" value={fmt(data.ad_ctr, { pct: true, decimals: 2 })}/>
                <PendingTile label="eCPM" reason={data.ecpm_pence?.reason}/>

                <Stat label="Gift cards sold" value={fmt(data.gift_cards_sold)}/>
                <Stat label="Gift cards redeemed" value={fmt(data.gift_cards_redeemed)}/>
                <Stat label="Unredeemed balance" value={gbp(data.gift_cards_unredeemed_balance_pence)}/>
                <PendingTile label="Refund rate" reason={data.refund_rate?.reason}/>
            </div>

            <Card title="Plan mix · live subscribers">
                <BarTable rows={data.plan_mix || []}
                          cols={[
                              { key: 'tier', label: 'Tier', className: 'capitalize font-semibold' },
                              { key: 'billing_period', label: 'Billing', className: 'capitalize' },
                              { key: 'count', label: 'Subs', right: true, bar: true },
                          ]}
                          empty="No subscriptions yet." />
            </Card>
        </section>
    );
}


// =================== Module: Finance =========================================

function FinanceModule() {
    const { data, err, loading, reload } = useApi('/admin/insights/finance');
    const [drill, setDrill] = useState(null); // 'pending' | 'overdue' | null
    const [drillRows, setDrillRows] = useState([]);
    const [drillLoading, setDrillLoading] = useState(false);

    const openDrill = async (statusFilter) => {
        setDrill(statusFilter);
        setDrillLoading(true);
        try {
            const { data: r } = await api.get('/admin/invoices', { params: { status: statusFilter } });
            setDrillRows(r.invoices || []);
        } finally { setDrillLoading(false); }
    };

    const compOne = async (inv) => {
        if (!window.confirm(`Comp invoice ${inv.invoice_number} for "${inv.school_name || 'this school'}"?\n\nMarks as £0 PILOT, removes from outstanding, and activates the school.`)) return;
        try {
            await api.post(`/admin/invoices/${inv.id}/comp`);
            openDrill(drill); reload();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    };
    const voidOne = async (inv) => {
        if (!window.confirm(`Void invoice ${inv.invoice_number}?`)) return;
        try {
            await api.post(`/admin/invoices/${inv.id}/void`);
            openDrill(drill); reload();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    };
    const markPaidOne = async (inv) => {
        const ref = window.prompt('Bank reference / PayPal transaction id?');
        if (ref === null) return;
        try {
            await api.post(`/admin/invoices/${inv.id}/mark-paid`, { paid_reference: ref });
            openDrill(drill); reload();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    };

    if (loading) return <Loading/>;
    if (err) return <ErrorBox msg={err}/>;
    const lines = data.revenue_by_product_line_pence || {};
    return (
        <section className="space-y-6" data-testid="module-finance">
            <Header title="Finance &amp; revenue operations" onReload={reload}/>
            <p className="text-xs text-lq-ink-500">Pilots and test accounts are excluded from outstanding + MRR totals (set the flags from the Schools / Users tabs to keep the numbers honest).</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Invoices total" value={fmt(data.invoices_total)}/>
                <Stat label="Paid" value={fmt(data.invoices_paid)} accent="text-lq-success"/>
                <button onClick={() => openDrill('pending')} className="text-left" data-testid="drill-pending"><Stat label="Pending (click to drill)" value={fmt(data.invoices_pending)} sub="see who"/></button>
                <button onClick={() => openDrill('overdue')} className="text-left" data-testid="drill-overdue"><Stat label="Overdue (click to drill)" value={fmt(data.invoices_overdue)} accent={data.invoices_overdue > 0 ? 'text-lq-error' : ''} sub="see who"/></button>

                <Stat label="Cash collected" value={gbp(data.cash_collected_pence)} sub="from paid invoices" accent="text-lq-purple-700"/>
                <button onClick={() => openDrill('pending')} className="text-left" data-testid="drill-outstanding"><Stat label="Outstanding (click to drill)" value={gbp(data.outstanding_pence)} sub="pending + overdue · click to verify"/></button>
                <Stat label="Payment success" value={fmt(data.payment_success_rate, { pct: true })} sub="paid / total invoices"/>
                <Stat label="Failed billing attempts" value={fmt(data.failed_billing_attempts)}/>
            </div>

            <Card title="Revenue by product line">
                <div className="grid sm:grid-cols-3 gap-3">
                    <Stat label="Subscriptions" value={gbp(lines.subscriptions)} sub="paypal lifetime estimate"/>
                    <Stat label="School invoices" value={gbp(lines.schools_invoices)} sub="paid invoices"/>
                    <Stat label="Gift cards" value={gbp(lines.gift_cards)} sub="issued + redeemed"/>
                </div>
            </Card>

            <div className="grid sm:grid-cols-2 gap-3">
                <PendingTile label="Refund rate" reason={data.refund_rate?.reason}/>
                <PendingTile label="Chargebacks" reason={data.chargebacks?.reason}/>
            </div>

            {drill && (
                <div className="fixed inset-0 bg-lq-ink-black/50 flex items-center justify-center p-4 z-50" onClick={() => setDrill(null)}>
                    <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="invoice-drill-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-2xl font-semibold text-lq-ink-900 capitalize">{drill} invoices — drill in</h3>
                            <button onClick={() => setDrill(null)} className="text-lq-ink-500 hover:text-lq-ink-900 text-2xl leading-none">×</button>
                        </div>
                        <p className="text-xs text-lq-ink-500 mb-3">Verify each invoice. Use <strong>Comp</strong> for word-of-mouth pilots (removes from outstanding, keeps the paper trail). Use <strong>Void</strong> for mis-issued invoices. <strong>Mark paid</strong> once cash arrives.</p>
                        {drillLoading ? <Loading/> : (
                            <table className="w-full text-sm">
                                <thead className="bg-lq-purple-50 text-lq-purple-900 text-xs uppercase tracking-wider">
                                    <tr><th className="text-left px-3 py-2">Invoice</th><th className="text-left px-3 py-2">School</th><th className="text-right px-3 py-2">Amount</th><th className="text-left px-3 py-2">Created</th><th className="text-right px-3 py-2">Actions</th></tr>
                                </thead>
                                <tbody className="divide-y divide-lq-ink-100">
                                    {drillRows.length === 0 && <tr><td colSpan="5" className="px-3 py-6 text-center text-lq-ink-500">Nothing in this bucket.</td></tr>}
                                    {drillRows.map((i) => (
                                        <tr key={i.id} data-testid={`drill-row-${i.id}`}>
                                            <td className="px-3 py-2 font-mono text-xs">{i.invoice_number}</td>
                                            <td className="px-3 py-2">
                                                <div className="text-sm">{i.school_name || <span className="text-lq-ink-500">—</span>}</div>
                                                {i.school_billing_email && <div className="text-[10px] text-lq-ink-500 font-mono">{i.school_billing_email}</div>}
                                            </td>
                                            <td className="px-3 py-2 text-right">£{(i.amount_cents/100).toFixed(2)}</td>
                                            <td className="px-3 py-2 text-xs">{new Date(i.created_at).toLocaleDateString()}</td>
                                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                                <button onClick={() => markPaidOne(i)} className="text-xs text-lq-purple-700 hover:underline mr-3" data-testid={`drill-mark-paid-${i.id}`}>Mark paid</button>
                                                <button onClick={() => compOne(i)} className="text-xs text-lq-success hover:underline mr-3" data-testid={`drill-comp-${i.id}`}>Comp</button>
                                                <button onClick={() => voidOne(i)} className="text-xs text-lq-error hover:underline" data-testid={`drill-void-${i.id}`}>Void</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}


// =================== Module: Content =========================================

function ContentModule() {
    const { data, err, loading, reload } = useApi('/admin/insights/content');
    if (loading) return <Loading/>;
    if (err) return <ErrorBox msg={err}/>;
    return (
        <section className="space-y-6" data-testid="module-content">
            <Header title="Content &amp; course performance" onReload={reload}/>

            <Card title="Course demand by language · 30d">
                <BarTable rows={data.demand_by_language_30d || []}
                          cols={[
                              { key: 'language_slug', label: 'Language', className: 'capitalize' },
                              { key: 'active_learners', label: 'Learners', right: true },
                              { key: 'plays', label: 'Plays', right: true, bar: true },
                          ]}
                          empty="No course activity yet — students haven't played any puzzles in the last 30 days." />
            </Card>

            <Card title="Puzzle performance · 30d">
                <BarTable rows={data.puzzle_performance_30d || []}
                          cols={[
                              { key: 'puzzle_type', label: 'Puzzle', className: 'capitalize' },
                              { key: 'plays', label: 'Plays', right: true },
                              { key: 'avg_attempts', label: 'Avg attempts', right: true, bar: true },
                          ]}
                          empty="No puzzle plays yet." />
            </Card>

            <Card title="Country quiz accuracy · top 25 (30d)">
                <BarTable rows={data.country_quiz_accuracy_30d || []}
                          cols={[
                              { key: 'country_slug', label: 'Country', className: 'capitalize' },
                              { key: 'attempts', label: 'Attempts', right: true },
                              { key: 'correct', label: 'Correct', right: true },
                              { key: 'accuracy', label: 'Accuracy', right: true,
                                format: (v) => `${(v * 100).toFixed(0)}%`, bar: true, barValue: (r) => r.accuracy },
                          ]}
                          empty="No quiz attempts yet." />
            </Card>

            <div className="grid sm:grid-cols-3 gap-3">
                <PendingTile label="Content bugs" reason={data.content_bugs_open?.reason}/>
                <PendingTile label="A/B test results" reason={data.ab_test_results?.reason}/>
                <PendingTile label="Course retention" reason={data.course_level_retention?.reason}/>
            </div>
        </section>
    );
}


// =================== Module: Support =========================================

function SupportModule() {
    const { data, err, loading, reload } = useApi('/admin/insights/support');
    const [dryRunning, setDryRunning] = useState(false);
    const triggerWeekly = async () => {
        const target = window.prompt('Send to a single parent (email)? Leave blank to send to all opted-in parents.');
        if (target === null) return;
        setDryRunning(true);
        try {
            const { data } = await api.post('/admin/ops/weekly-recap/run', {
                target_parent_email: target ? target.trim() : null,
                force: true,
            });
            if (data.reason === 'no_activity_or_opted_out') {
                toast.warning('No digest sent — that family has no activity this week or has opted out.');
            } else if (data.sent === 0) {
                toast.warning('Nothing was queued (run was already completed for this week).');
            } else {
                toast.success(`${data.sent} digest${data.sent === 1 ? '' : 's'} queued.`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not trigger digest');
        } finally { setDryRunning(false); }
    };
    if (loading) return <Loading/>;
    if (err) return <ErrorBox msg={err}/>;
    const avg = data.avg_response_time_seconds_30d;
    const avgHuman = avg == null ? '—' : (avg < 60 ? `${avg}s` : avg < 3600 ? `${Math.round(avg / 60)}m` : `${(avg / 3600).toFixed(1)}h`);
    return (
        <section className="space-y-6" data-testid="module-support">
            <Header
                title="Support &amp; platform health"
                onReload={reload}
                action={
                    <button onClick={triggerWeekly} disabled={dryRunning} className="px-4 py-2 text-sm font-semibold bg-lq-peach-500 text-white rounded-full hover:bg-lq-peach-600 disabled:opacity-50 inline-flex items-center gap-1" data-testid="trigger-weekly-recap">
                        {dryRunning && <Loader2 size={14} className="animate-spin"/>} Send weekly recap now
                    </button>
                }
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Open tickets" value={fmt(data.open_tickets)} accent={data.open_tickets > 10 ? 'text-lq-error' : 'text-lq-ink-900'}/>
                <Stat label="Opened in 24h" value={fmt(data.open_24h)}/>
                <Stat label="Avg response (30d)" value={avgHuman}/>
                <Link to="/admin#contact" className="bg-lq-purple-50 hover:bg-lq-purple-100 rounded-2xl border border-lq-purple-100 p-5 block">
                    <div className="text-[11px] text-lq-purple-700 uppercase tracking-wider font-semibold">Go to inbox</div>
                    <div className="mt-1 font-display text-lg text-lq-purple-900">Open Contact tab →</div>
                </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <PendingTile label="Crash rate" reason={data.crash_rate?.reason}/>
                <PendingTile label="API latency p50" reason={data.api_latency_p50?.reason}/>
                <PendingTile label="Lesson load time" reason={data.lesson_load_time?.reason}/>
                <PendingTile label="Uptime 30d" reason={data.uptime_pct_30d?.reason}/>
                <PendingTile label="Login failures 24h" reason={data.login_failures_24h?.reason}/>
                <PendingTile label="Moderation flags" reason={data.moderation_flags_open?.reason}/>
                <PendingTile label="Fraud alerts" reason={data.fraud_alerts?.reason}/>
            </div>
        </section>
    );
}


// =================== Module: Admin accounts (super-admin only) ==============

function AdminsModule() {
    const { data, err, loading, reload } = useApi('/admin/admins');
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ email: '', display_name: '', admin_role: 'finance', password: '' });
    const [submitting, setSubmitting] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post('/admin/admins', form);
            toast.success(`${form.admin_role} admin created`);
            setForm({ email: '', display_name: '', admin_role: 'finance', password: '' });
            setShowForm(false);
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not create admin');
        } finally { setSubmitting(false); }
    };
    const revoke = async (a) => {
        if (!window.confirm(`Revoke ${a.admin_role} admin access for ${a.email}?`)) return;
        try {
            await api.delete(`/admin/admins/${a.id}`);
            toast.success('Revoked');
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not revoke');
        }
    };
    if (loading) return <Loading/>;
    if (err) return <ErrorBox msg={err}/>;
    return (
        <section className="space-y-6" data-testid="module-admins">
            <Header
                title="Admin accounts"
                sub="Create or revoke Finance / Content / Support admins. Password is set manually and shared out-of-band."
                onReload={reload}
                action={<button onClick={() => setShowForm((v) => !v)} className="px-4 py-2 text-sm font-semibold bg-lq-purple-500 text-white rounded-full hover:bg-lq-purple-700" data-testid="new-admin-toggle">{showForm ? 'Close' : 'New admin'}</button>}
            />

            {showForm && (
                <form onSubmit={submit} className="bg-lq-purple-50/60 border border-lq-purple-100 rounded-2xl p-5 grid sm:grid-cols-2 gap-3" data-testid="new-admin-form">
                    <label className="block">
                        <span className="text-xs font-medium text-lq-ink-900">Display name</span>
                        <input required value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="new-admin-name"/>
                    </label>
                    <label className="block">
                        <span className="text-xs font-medium text-lq-ink-900">Email</span>
                        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="new-admin-email"/>
                    </label>
                    <label className="block">
                        <span className="text-xs font-medium text-lq-ink-900">Role</span>
                        <select value={form.admin_role} onChange={(e) => setForm({ ...form, admin_role: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2 bg-white" data-testid="new-admin-role">
                            <option value="finance">Finance admin</option>
                            <option value="content">Content admin</option>
                            <option value="support">Support &amp; Trust admin</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-xs font-medium text-lq-ink-900">Password (8+ chars — share securely)</span>
                        <input required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2 font-mono" data-testid="new-admin-password"/>
                    </label>
                    <div className="sm:col-span-2 flex justify-end">
                        <button type="submit" disabled={submitting} className="px-5 py-2 text-sm font-semibold rounded-full bg-lq-purple-500 text-white hover:bg-lq-purple-700 disabled:opacity-50 inline-flex items-center gap-2" data-testid="new-admin-submit">
                            {submitting && <Loader2 size={14} className="animate-spin"/>} Create admin
                        </button>
                    </div>
                </form>
            )}

            <div className="bg-white border border-lq-ink-100 rounded-2xl overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-lq-purple-50 text-lq-purple-900 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-2">Name</th>
                            <th className="text-left px-4 py-2">Email</th>
                            <th className="text-left px-4 py-2">Role</th>
                            <th className="text-left px-4 py-2">Created</th>
                            <th className="text-right px-4 py-2"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-lq-ink-100">
                        {(data.admins || []).map((a) => {
                            const role = a.admin_role || 'super_admin';
                            return (
                                <tr key={a.id} data-testid={`admin-row-${a.id}`}>
                                    <td className="px-4 py-2 font-medium">{a.display_name || '—'}</td>
                                    <td className="px-4 py-2 font-mono text-xs">{a.email}</td>
                                    <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${role === 'super_admin' ? 'bg-lq-purple-100 text-lq-purple-700' : 'bg-lq-sky-100 text-lq-sky-600'}`}>{role}</span></td>
                                    <td className="px-4 py-2 text-xs">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
                                    <td className="px-4 py-2 text-right">
                                        {role !== 'super_admin' && (
                                            <button onClick={() => revoke(a)} className="text-xs text-lq-error hover:underline" data-testid={`revoke-admin-${a.id}`}>Revoke</button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}


// =================== Module: CSV exports =====================================

function ExportsModule({ user }) {
    const tiles = [
        { label: 'Users', file: 'users.csv', desc: 'Parents, students, teachers (no PII passwords).', requires: ['finance', 'support'] },
        { label: 'Invoices', file: 'invoices.csv', desc: 'School invoices, statuses, amounts.', requires: ['finance'] },
        { label: 'Subscriptions', file: 'subscriptions.csv', desc: 'PayPal subscription rollup.', requires: ['finance'] },
        { label: 'Gift cards', file: 'gift-cards.csv', desc: 'Issued + redeemed gift cards.', requires: ['finance'] },
    ];
    const allowed = (req) => isSuper(user) || (user.admin_role && req.includes(user.admin_role));
    const download = async (file) => {
        try {
            const res = await api.get(`/admin/export/${file}`, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = file;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`${file} downloaded`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not download');
        }
    };
    return (
        <section className="space-y-6" data-testid="module-exports">
            <Header title="CSV exports" sub="Live snapshots, downloaded to your device. Re-export any time."/>
            <div className="grid sm:grid-cols-2 gap-3">
                {tiles.map((t) => {
                    const can = allowed(t.requires);
                    return (
                        <div key={t.file} className={`rounded-2xl border p-5 ${can ? 'bg-white border-lq-ink-100' : 'bg-lq-ink-100/40 border-dashed border-lq-ink-300'}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="font-display text-lg font-semibold text-lq-ink-900">{t.label}</div>
                                    <p className="text-sm text-lq-ink-500 mt-1">{t.desc}</p>
                                </div>
                                {can ? (
                                    <button onClick={() => download(t.file)} className="px-4 py-2 text-sm font-semibold bg-lq-purple-500 text-white rounded-full hover:bg-lq-purple-700 inline-flex items-center gap-1" data-testid={`export-${t.file.replace('.csv', '')}`}>
                                        <Download size={14}/> Download
                                    </button>
                                ) : (
                                    <span className="text-xs text-lq-ink-500 inline-flex items-center gap-1"><Lock size={12}/> Role-locked</span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}


// =================== Shared bits =============================================

function Loading() {
    return <div className="py-10 text-center text-lq-ink-500"><Loader2 className="inline-block animate-spin"/> Loading…</div>;
}

function ErrorBox({ msg }) {
    return <div className="py-6 bg-lq-error/10 border border-lq-error rounded-2xl p-5 text-lq-error">{msg}</div>;
}

function Header({ title, sub, onReload, action }) {
    return (
        <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
                <h2 className="font-display text-2xl text-lq-ink-900">{title}</h2>
                {sub && <p className="text-sm text-lq-ink-500 mt-0.5">{sub}</p>}
            </div>
            <div className="flex items-center gap-2">
                {onReload && (
                    <button onClick={onReload} className="text-sm text-lq-ink-500 hover:text-lq-ink-900 inline-flex items-center gap-1" data-testid="module-reload">
                        <RefreshCw size={14}/> Refresh
                    </button>
                )}
                {action}
            </div>
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="bg-white rounded-2xl border border-lq-ink-100 p-5">
            <h3 className="font-display font-semibold text-lq-ink-900 mb-3">{title}</h3>
            {children}
        </div>
    );
}

function BarTable({ rows, cols, empty }) {
    if (!rows || rows.length === 0) return <p className="text-sm text-lq-ink-500">{empty}</p>;
    const barCol = cols.find((c) => c.bar);
    const maxVal = barCol ? Math.max(...rows.map((r) => (barCol.barValue ? barCol.barValue(r) : (Number(r[barCol.key]) || 0)))) || 1 : 1;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-xs text-lq-ink-500 uppercase tracking-wider">
                        {cols.map((c) => (
                            <th key={c.key} className={`text-${c.right ? 'right' : 'left'} py-1.5 px-2`}>{c.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-lq-ink-100">
                    {rows.map((r, i) => (
                        <tr key={i}>
                            {cols.map((c) => {
                                const raw = r[c.key];
                                const value = c.format ? c.format(raw) : (typeof raw === 'number' ? raw.toLocaleString() : (raw ?? '—'));
                                const align = c.right ? 'text-right' : 'text-left';
                                return (
                                    <td key={c.key} className={`py-1.5 px-2 ${align} ${c.className || ''}`}>
                                        {c.bar ? (
                                            <div className="flex items-center justify-end gap-2">
                                                <span>{value}</span>
                                                <span className="block w-20 h-1.5 bg-lq-purple-100 rounded-full overflow-hidden">
                                                    <span className="block h-full bg-lq-purple-500" style={{ width: `${100 * ((c.barValue ? c.barValue(r) : (Number(raw) || 0)) / maxVal)}%` }}/>
                                                </span>
                                            </div>
                                        ) : value}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Sparkline({ series, labels }) {
    if (!series || series.length === 0) return null;
    const max = Math.max(...series) || 1;
    return (
        <div>
            <div className="flex items-end gap-1 h-24">
                {series.map((v, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${labels?.[i] || ''}: ${v}`}>
                        <div className="w-full bg-lq-purple-500 rounded-t-sm" style={{ height: `${(v / max) * 100}%`, minHeight: v > 0 ? '2px' : 0 }}/>
                    </div>
                ))}
            </div>
            <div className="mt-2 flex justify-between text-[10px] text-lq-ink-500 font-mono">
                <span>{labels?.[0]}</span>
                <span>peak {max}</span>
                <span>{labels?.[labels.length - 1]}</span>
            </div>
        </div>
    );
}


// ================= AUDIENCE MAP =================
// Marketable stat — visible to every admin role.
// Used to answer "where are our users?" for sponsor / advertiser conversations.

function AudienceModule() {
    const { data, err, loading, reload } = useApi('/admin/insights/audience');
    if (loading) return <Loading/>;
    if (err) return <ErrorBox msg={err}/>;

    const t = data.totals || {};
    const rows = data.countries || [];
    const trend = data.signup_trend_6m || [];

    const exportCsv = () => {
        const header = 'country_code,flag,adults,children,total_learners,paying_adults,conversion_pct\n';
        const body = rows.map(r => `${r.country_code},${r.flag},${r.adults},${r.children},${r.total_learners},${r.paying_adults},${r.conversion_pct}`).join('\n');
        const blob = new Blob([header + body], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `linguistic-quest-audience-${new Date().toISOString().slice(0,10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <section className="space-y-6" data-testid="module-audience">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl font-semibold text-lq-ink-900 inline-flex items-center gap-2"><Globe size={22}/> Audience map</h2>
                    <p className="text-sm text-lq-ink-500">Where our learners live. Safe to share with sponsors, advertisers &amp; partners — no personal data below.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={exportCsv} className="text-xs px-3 py-1.5 rounded-full bg-lq-purple-100 text-lq-purple-700 font-semibold hover:bg-lq-purple-500 hover:text-white inline-flex items-center gap-1" data-testid="audience-export-csv">
                        <Download size={12}/> Export CSV
                    </button>
                    <button onClick={reload} className="text-xs px-3 py-1.5 rounded-full bg-lq-ink-100 text-lq-ink-700 hover:bg-lq-purple-100 inline-flex items-center gap-1"><RefreshCw size={12}/> Reload</button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Stat label="Countries reached" value={fmt(t.countries_reached)} accent="text-lq-purple-700"/>
                <Stat label="Adult learners" value={fmt(t.adults)} sub="account holders"/>
                <Stat label="Child learners" value={fmt(t.children)} sub="under-16 profiles" accent="text-lq-peach-600"/>
                <Stat label="Total learners" value={fmt(t.learners)} accent="text-lq-success"/>
                <Stat label="Paying adults" value={fmt(t.paying_adults)} sub="active + trialing"/>
            </div>

            <Card title="Top countries by learners">
                <div className="bg-white rounded-xl border border-lq-ink-100 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="audience-table">
                        <thead className="bg-lq-purple-50 text-lq-purple-900 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3">Country</th>
                                <th className="text-right px-4 py-3">Adults</th>
                                <th className="text-right px-4 py-3">Children</th>
                                <th className="text-right px-4 py-3">Total</th>
                                <th className="text-right px-4 py-3">Paying</th>
                                <th className="text-right px-4 py-3">Conv %</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-lq-ink-100">
                            {rows.length === 0 && <tr><td colSpan="6" className="px-4 py-6 text-center text-lq-ink-500">No user data yet.</td></tr>}
                            {rows.map((r) => (
                                <tr key={r.country_code} data-testid={`audience-row-${r.country_code}`}>
                                    <td className="px-4 py-3">
                                        <span className="text-xl mr-2">{r.flag || '🏳️'}</span>
                                        <span className="font-medium">{r.country_code}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right">{r.adults.toLocaleString()}</td>
                                    <td className="px-4 py-3 text-right">
                                        <span className="inline-flex items-center gap-1 text-lq-peach-600"><Baby size={12}/> {r.children.toLocaleString()}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right font-semibold">{r.total_learners.toLocaleString()}</td>
                                    <td className="px-4 py-3 text-right text-lq-success">{r.paying_adults.toLocaleString()}</td>
                                    <td className="px-4 py-3 text-right">
                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.conversion_pct >= 10 ? 'bg-lq-success/15 text-lq-success' : 'bg-lq-ink-100 text-lq-ink-700'}`}>{r.conversion_pct}%</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Card title="Signup trend — last 6 months">
                {trend.length === 0 ? (
                    <p className="text-sm text-lq-ink-500">No signups in the last 6 months.</p>
                ) : (
                    <Sparkline
                        values={trend.map(t => t.signups)}
                        labels={trend.map(t => t.month)}
                    />
                )}
            </Card>
        </section>
    );
}


// ================= ATTRIBUTION INSIGHTS (SUPER-ADMIN) =================

function AttributionModule() {
    const { data: attribution, err, loading, reload } = useApi('/admin/insights/attribution');
    const { data: ipData, reload: reloadIp } = useApi('/admin/insights/ip-intelligence');

    if (loading) return <Loading/>;
    if (err) return <ErrorBox msg={err}/>;
    const funnel = attribution.affiliate_funnel || [];
    const device = attribution.device_mix || { os: [], browser: [] };
    const traffic = attribution.traffic_sources || [];
    const consent = attribution.consent || {};

    return (
        <section className="space-y-6" data-testid="module-attribution">
            <div>
                <h2 className="font-display text-2xl font-semibold text-lq-ink-900 inline-flex items-center gap-2"><Radar size={22}/> Attribution insights</h2>
                <p className="text-sm text-lq-ink-500">Super-admin only. Aggregated marketing / device / consent data from what we already capture. No IP, no PII.</p>
            </div>

            <Card title="Affiliate funnel">
                {funnel.length === 0 ? (
                    <p className="text-sm text-lq-ink-500">No approved affiliates yet.</p>
                ) : (
                    <div className="bg-white rounded-xl border border-lq-ink-100 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-lq-purple-50 text-lq-purple-900 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="text-left px-4 py-3">Code</th>
                                    <th className="text-left px-4 py-3">Name</th>
                                    <th className="text-right px-4 py-3">Clicks</th>
                                    <th className="text-right px-4 py-3">Signups</th>
                                    <th className="text-right px-4 py-3">Active</th>
                                    <th className="text-right px-4 py-3">Click→Signup</th>
                                    <th className="text-right px-4 py-3">Signup→Paid</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-lq-ink-100">
                                {funnel.map((a) => (
                                    <tr key={a.code} data-testid={`attr-funnel-${a.code}`}>
                                        <td className="px-4 py-3 font-mono">{a.code}</td>
                                        <td className="px-4 py-3">{a.name}</td>
                                        <td className="px-4 py-3 text-right">{a.clicks}</td>
                                        <td className="px-4 py-3 text-right">{a.signups}</td>
                                        <td className="px-4 py-3 text-right text-lq-success">{a.active_subs}</td>
                                        <td className="px-4 py-3 text-right">{a.click_to_signup_pct}%</td>
                                        <td className="px-4 py-3 text-right">{a.signup_to_paid_pct}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <div className="grid md:grid-cols-2 gap-3">
                <Card title="Operating system mix">
                    {device.os.length === 0 ? <p className="text-sm text-lq-ink-500">No click data yet.</p> : (
                        <ul className="space-y-1.5 text-sm">
                            {device.os.map(o => <li key={o.label} className="flex justify-between border-b border-lq-ink-100 pb-1.5"><span>{o.label}</span><span className="font-mono">{o.count}</span></li>)}
                        </ul>
                    )}
                </Card>
                <Card title="Browser mix">
                    {device.browser.length === 0 ? <p className="text-sm text-lq-ink-500">No click data yet.</p> : (
                        <ul className="space-y-1.5 text-sm">
                            {device.browser.map(b => <li key={b.label} className="flex justify-between border-b border-lq-ink-100 pb-1.5"><span>{b.label}</span><span className="font-mono">{b.count}</span></li>)}
                        </ul>
                    )}
                </Card>
            </div>

            <Card title="Traffic sources (affiliate clicks)">
                {traffic.length === 0 ? <p className="text-sm text-lq-ink-500">No click data yet.</p> : (
                    <ul className="space-y-1.5 text-sm">
                        {traffic.map(t => <li key={t.source} className="flex justify-between border-b border-lq-ink-100 pb-1.5"><span>{t.source}</span><span className="font-mono">{t.count}</span></li>)}
                    </ul>
                )}
            </Card>

            <Card title="Marketing consent audit">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <Stat label="Total parents" value={fmt(consent.total_parents)}/>
                    <Stat label="Opted-in" value={fmt(consent.marketing_opted_in)} accent="text-lq-success"/>
                    <Stat label="Opt-in rate" value={fmt(consent.opt_in_rate_pct, { pct: true })}/>
                    <Stat label="Consent quality" value={consent.opt_in_rate_pct > 20 ? '✓ healthy' : '⚠ low'} accent={consent.opt_in_rate_pct > 20 ? 'text-lq-success' : 'text-lq-warn'}/>
                </div>
                <div className="text-xs text-lq-ink-500 mb-2">Opted-in parents by country (top 15):</div>
                <div className="flex flex-wrap gap-2">
                    {(consent.by_country || []).map(c => (
                        <span key={c.country_code} className="text-xs bg-lq-ink-100 px-2 py-1 rounded-full">
                            {c.flag} {c.country_code} — {c.count}
                        </span>
                    ))}
                    {(!consent.by_country || consent.by_country.length === 0) && <span className="text-sm text-lq-ink-500">No opt-ins yet.</span>}
                </div>
            </Card>

            {/* IP intelligence — legal-caveat CTA */}
            <Card title="Session &amp; IP intelligence">
                {ipData?.enabled ? (
                    <div className="bg-lq-success/10 border border-lq-success/40 rounded-lg p-4 text-sm">
                        <CheckCircle2 size={20} className="inline text-lq-success mr-2"/>
                        IP logging is enabled. Detailed aggregations in v2.
                    </div>
                ) : (
                    <div className="bg-lq-warn/10 border border-lq-warn/40 rounded-lg p-4" data-testid="ip-intel-locked">
                        <div className="flex items-start gap-2 mb-3">
                            <AlertTriangle size={20} className="text-lq-warn flex-shrink-0 mt-0.5"/>
                            <div className="text-sm text-lq-ink-700">
                                <strong className="text-lq-warn">Not currently collecting IP data.</strong>
                                <p className="mt-1 text-xs">{ipData?.legal_note}</p>
                            </div>
                        </div>
                        <details className="text-xs text-lq-ink-700 mt-3">
                            <summary className="cursor-pointer font-semibold text-lq-purple-700 hover:underline">What we already capture</summary>
                            <ul className="mt-2 list-disc pl-5 space-y-1">
                                {(ipData?.current_data_captured || []).map((c, i) => <li key={i}>{c}</li>)}
                            </ul>
                        </details>
                        <details className="text-xs text-lq-ink-700 mt-2">
                            <summary className="cursor-pointer font-semibold text-lq-purple-700 hover:underline">If we enable IP logging</summary>
                            <div className="mt-2">
                                <p className="font-semibold mb-1">New captures:</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    {(ipData?.proposed_new_captures_when_enabled || []).map((c, i) => <li key={i}>{c}</li>)}
                                </ul>
                                <p className="font-semibold mb-1 mt-3">Business use-cases:</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    {(ipData?.business_use_cases_unlocked || []).map((c, i) => <li key={i}>{c}</li>)}
                                </ul>
                            </div>
                        </details>
                    </div>
                )}
            </Card>
        </section>
    );
}


// =================== Module: Deletions & churn (super-admin only) ===========

function DeletionsModule() {
    const [days, setDays] = useState(30);
    const { data, err, loading, reload } = useApi(`/admin/deletions/recent?days=${days}`, [days]);
    const [restoringId, setRestoringId] = useState(null);

    const REASON_LABELS = {
        too_expensive: 'Too expensive',
        not_using: 'Not using it enough',
        child_lost_interest: 'Child lost interest',
        bugs_issues: 'Bugs / issues',
        privacy: 'Privacy',
        other: 'Other',
    };
    const TRIGGER_LABELS = {
        self_service: 'User self-service',
        admin: 'Admin action',
    };

    const restore = async (row) => {
        if (!window.confirm(`Restore ${row.pre_deletion_email || row.id}? Their account, subscription cancel-request and child profiles will all be reactivated.`)) return;
        setRestoringId(row.id);
        try {
            const { data: res } = await api.post(`/admin/users/${row.id}/restore`);
            toast.success(`Restored ${res.restored_email}.`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Restore failed');
        } finally { setRestoringId(null); }
    };

    if (loading) return <Loading/>;
    if (err) return <ErrorBox msg={err}/>;
    const rows = data?.deletions || [];

    const reasonCounts = {};
    for (const r of rows) {
        const key = r.deletion_reason || 'not_given';
        reasonCounts[key] = (reasonCounts[key] || 0) + 1;
    }
    const reasonRows = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1]);

    return (
        <section className="space-y-6" data-testid="module-deletions">
            <Header
                title="Deletions &amp; churn"
                sub={`${rows.length} account${rows.length === 1 ? '' : 's'} deleted in the last ${days} days. 30-day grace window before hard-purge.`}
                onReload={reload}
                action={
                    <select
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        className="text-sm px-3 py-1.5 rounded-full bg-white border border-lq-ink-100"
                        data-testid="deletions-window-select"
                    >
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={60}>Last 60 days</option>
                        <option value={90}>Last 90 days</option>
                    </select>
                }
            />

            {rows.length === 0 ? (
                <div className="bg-white rounded-2xl border border-lq-ink-100 p-8 text-center">
                    <CheckCircle2 size={32} className="mx-auto text-lq-success mb-2"/>
                    <div className="font-display text-lg text-lq-ink-900">No deletions in this window.</div>
                    <div className="text-sm text-lq-ink-500 mt-1">Parents are staying with you. Nice work.</div>
                </div>
            ) : (
                <>
                    <Card title="Reason breakdown">
                        <ul className="space-y-1.5 text-sm" data-testid="deletions-reason-breakdown">
                            {reasonRows.map(([slug, count]) => (
                                <li key={slug} className="flex justify-between border-b border-lq-ink-100 pb-1.5">
                                    <span>{REASON_LABELS[slug] || (slug === 'not_given' ? 'No reason given' : slug)}</span>
                                    <span className="font-mono">{count}</span>
                                </li>
                            ))}
                        </ul>
                    </Card>

                    <div className="bg-white rounded-2xl border border-lq-ink-100 overflow-x-auto">
                        <table className="w-full text-sm" data-testid="deletions-table">
                            <thead className="bg-lq-purple-50 text-lq-purple-900 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="text-left px-4 py-3">Deleted</th>
                                    <th className="text-left px-4 py-3">Name / email</th>
                                    <th className="text-left px-4 py-3">Trigger</th>
                                    <th className="text-left px-4 py-3">Reason</th>
                                    <th className="text-left px-4 py-3">Feedback</th>
                                    <th className="text-right px-4 py-3">Grace left</th>
                                    <th className="text-right px-4 py-3">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-lq-ink-100">
                                {rows.map((r) => (
                                    <tr key={r.id} data-testid={`deletion-row-${r.id}`}>
                                        <td className="px-4 py-3 text-xs whitespace-nowrap">{r.deleted_at ? new Date(r.deleted_at).toLocaleDateString() : '—'}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-lq-ink-900">{r.pre_deletion_display_name || '—'}</div>
                                            <div className="text-xs text-lq-ink-500 font-mono">{r.pre_deletion_email || '—'}</div>
                                        </td>
                                        <td className="px-4 py-3 text-xs">{TRIGGER_LABELS[r.deletion_triggered_by] || (r.deletion_triggered_by || '—')}</td>
                                        <td className="px-4 py-3 text-xs">{REASON_LABELS[r.deletion_reason] || (r.deletion_reason || '—')}</td>
                                        <td className="px-4 py-3 text-xs max-w-xs">
                                            {r.deletion_feedback ? (
                                                <span title={r.deletion_feedback} className="text-lq-ink-700">
                                                    {r.deletion_feedback.length > 60 ? `${r.deletion_feedback.slice(0, 60)}…` : r.deletion_feedback}
                                                </span>
                                            ) : <span className="text-lq-ink-500">—</span>}
                                        </td>
                                        <td className="px-4 py-3 text-right text-xs whitespace-nowrap">
                                            {r.days_until_hard_purge != null ? (
                                                <span className={r.days_until_hard_purge <= 3 ? 'text-lq-error font-semibold' : r.days_until_hard_purge <= 7 ? 'text-lq-warn font-semibold' : 'text-lq-ink-700'}>
                                                    {r.days_until_hard_purge} day{r.days_until_hard_purge === 1 ? '' : 's'}
                                                </span>
                                            ) : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {r.restore_available ? (
                                                <button
                                                    onClick={() => restore(r)}
                                                    disabled={restoringId === r.id}
                                                    className="px-3 py-1.5 rounded-full bg-lq-purple-500 hover:bg-lq-purple-700 text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                                                    data-testid={`deletion-restore-${r.id}`}
                                                >
                                                    {restoringId === r.id ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>} Restore
                                                </button>
                                            ) : (
                                                <span className="text-xs text-lq-ink-500" title="Deleted before restore snapshotting was added — original identity is not recoverable.">n/a</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </section>
    );
}

